/* eslint-disable camelcase */
import * as React from "react"
import { useCallback, useMemo } from "react"
import * as yup from "yup"
import _ from "lodash"
import moment from "moment"
import numeral from "numeral"
import { Appearance, ViewStyle, Dimensions } from "react-native"
import { Cycle, Farm, Feed, FeedingTrayControl } from "../../../database"
import { DateTimePickerField } from "../../field/date-time-picker-field/date-time-picker-field"
import { FastField, FieldArray, Formik } from "formik"
import { FormControl, FormControlProps } from "../../form-control/form-control"
import { FormProps } from "../form"
import { IRadioGroupProps, Radio, View } from "native-base"
import { ModelField } from "../../field/model-field/model-field"
import { PickerField } from "../../field/picker-field/picker-field"
import { Text } from "../../text/text"
import { TextField } from "../../field/text-field/text-field"
import { TextStyle } from "react-native-phone-input"
import { color } from "../../../theme"
import { observer } from "mobx-react-lite"
import { translate, TxKeyPath } from "../../../i18n"
import { WalkthroughElement } from "../../walkthrough-element/walkthrough-element"

const windowWidth = Dimensions.get('window').width;

const REF_WRAPPER: ViewStyle = {
  width: windowWidth
}

const FORM_DROPDOWN: ViewStyle = {
  paddingHorizontal: 16,
  // marginBottom: 8
  paddingBottom: 8,
  backgroundColor: color.palette.white
}

const TITLE: ViewStyle = {
  borderTopWidth: 1,
  borderColor: color.palette.background,
  paddingHorizontal: 16,
  paddingTop: 16,
  paddingBottom: 10,
  backgroundColor: color.palette.white,
  width: Dimensions.get('window').width
}

const BORDER: ViewStyle = {
  backgroundColor: color.palette.background,
  height: 4,
  width: Dimensions.get('window').width
}

const TRAY_TITLE: ViewStyle = {
  flexDirection: 'row',
  paddingHorizontal: 16,
  marginBottom: 15,
  alignItems: 'center'
}

const REMARK: TextStyle = {
  marginHorizontal: 16
}

export interface FeedingTrayFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
   initialValues?: {
    farm_id?: number
    cycle_id?: number
    logged_at?: string,
    logged_date?: string
    feed_logged_at?: string
    logged_time?: string
    tray_number?: number
    feed_remaining_percent?: string
    feeding_tray_controls?: Array<any>
  }
}

const RadioField = (
  { name, colorScheme = Appearance.getColorScheme(), formControlProps, radioGroupProps }:
  { name: string, colorScheme?: "light" | "dark", formControlProps?: FormControlProps, radioGroupProps?: IRadioGroupProps },
) => (
  <FastField name={name}>
    {({ field, form: { setFieldValue, setFieldTouched }, meta }) => (
      <FormControl
        isInvalid={meta.touched && !!meta.error}
        labelTx={`feedingTrayControlForm.${_.camelCase(field.name)}` as TxKeyPath}
        colorScheme={colorScheme}
        labelColor={color.palette.gray800}
        errorMessage={meta.error}
        style={FORM_DROPDOWN}
        {...formControlProps}
      >
        <Radio.Group
          name={name}
          colorScheme={colorScheme}
          value={field.value}
          onChange={async (value) => {
            await setFieldValue(field.name, value || null);
            await setFieldTouched(field.name)
          }}
          {...radioGroupProps}
        >
          <Radio value="0" size="sm" my={1} colorScheme="blue">{_.startCase(translate('feedingTrayControls.empty'))}</Radio>
          <Radio value="33" size="sm" my={1} colorScheme="blue">{_.startCase(translate('feedingTrayControls.few'))}</Radio>
          <Radio value="66" size="sm" my={1} colorScheme="blue">{_.startCase(translate('feedingTrayControls.plenty'))}</Radio>
        </Radio.Group>
      </FormControl>
    )}
  </FastField>
)

/**
 * Describe your component here
 */
export const FeedingTrayControlForm = observer(function FeedingTrayForm(props: FeedingTrayFormProps) {
  const {
    initialValues: initialValuesProps = {},
    onSubmit: onSubmitProps,
    colorScheme = Appearance.getColorScheme(),
    innerRef,
    parentScrollViewRef: scrollViewRef,
    ...rest
  } = props

  const validationSchema = yup.object({
    farm_id: yup.number().nullable().required().label(translate("feedingTrayControlForm.farmId")),
    cycle_id: yup.number().nullable().required().label(translate("feedingTrayControlForm.cycleId")),
    logged_date: yup.string().nullable().required().label(translate("feedingTrayControlForm.loggedDate")),
    logged_time: yup.string().nullable().required().label(translate("feedingTrayControlForm.loggedTime")),
    feed_logged_at: yup.string().nullable().required().label(translate("feedingTrayControlForm.feedLoggedAt")),
    feeding_tray_controls: yup.array(yup.object({
      tray_number: yup.number().nullable().required(),
      feed_remaining_percent: yup.number().nullable()
    })).test(function (values) {
      const valid = !!values.find((v) => v.feed_remaining_percent != null)
      if (valid) return true;
      return this.createError({
        path: 'feeding_tray_controls',
        message: ''
      })
    }).required(),
    remark: yup.string().ascii().max(255).nullable().label(translate("feedingTrayControlForm.remark")),
  })

  const onSubmit = useCallback((values) => {
    onSubmitProps({
      ...values,
      logged_at: moment(`${values.logged_date} ${values.logged_time}`).utc().format('YYYY-MM-DD HH:mm:ss'),
      get logged_date() {
        return this.logged_at && moment.utc(this.logged_at).format('YYYY-MM-DD')
      },
      get logged_time() {
        return this.logged_at && moment.utc(this.logged_at).format('HH:mm:ss')
      },
    })
  }, [onSubmitProps])

  const initialValues = useMemo<FeedingTrayFormProps["initialValues"]>(() => ({
    farm_id: null,
    cycle_id: null,
    feed_logged_at: null,
    ...initialValuesProps,
    feeding_tray_controls: initialValuesProps.feeding_tray_controls.length && initialValuesProps.feeding_tray_controls || [{}],
    logged_at: initialValuesProps.logged_at,
    get logged_date() {
      return this.logged_at && moment.utc(this.logged_at).local().format('YYYY-MM-DD')
    },
    get logged_time() {
      return this.logged_at && moment.utc(this.logged_at).local().format('HH:mm:ss')
    },
  }), [initialValuesProps])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ values, initialValues, setFieldValue, setFieldTouched }) => (
        <>
          <ModelField
            name="farm_id"
            txPrefix="feedingTrayControlForm"
            colorScheme={colorScheme}
            formControlProps={{ isRequired: true }}
            modelPickerProps={{
              Model: Farm,
              isDisabled: !!initialValues.farm_id,
              onValueChange: async (value) => {
                await setFieldValue('feed_logged_at', null)
                await setFieldValue('logged_date', null)
                await setFieldValue('cycle_id', null)
                await setFieldValue('farm_id', value || null)
                await setFieldTouched('farm_id')
              }
            }}
          />
          <WalkthroughElement
            id="feeding-tray-form-first"
            scrollViewRef={scrollViewRef}
            scrollOffset={250}
          >
            <View style={TITLE}>
              <Text tx="feedingTrayControlForm.feedingTrayControl" preset="title3" colorScheme={colorScheme} />
            </View>
            <View style={BORDER} />
            <ModelField
              key={`${values.farm_id}-cycle_id`}
              name="cycle_id"
              txPrefix="feedingTrayControlForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              modelPickerProps={{
                Model: Cycle,
                dataLabel: "pond_name",
                fetchData: (model, search) => (
                  model.fetch({
                    farm_id__eq: values.farm_id,
                    sort: 'started_at,desc|pond_name_alpha_numeric'
                  }).then((models) => models.filter((m) => (
                    `${m.pond_name} - ${moment(m.started_at).format("DD MMM YYYY")}${m.finished_at ? ` (${translate("Finished")})` : ""}`
                  ).toLowerCase().includes((search || '').toLowerCase())))
                ),
                renderSelectedItemText: (item) => item && `${item.pond_name} - ${moment(item.started_at).format("DD MMM YYYY")}${item.finished_at ? ` (${translate("Finished")})` : ""}`,
                clearable: true,
                isDisabled: !values.farm_id || !!initialValues.cycle_id,
                onValueChange: async (value, index, item) => {
                  if (item)
                    await setFieldValue('feeding_tray_controls', _.map(
                      Array(item.feeding_tray_count || 0),
                      (v, index) => ({ tray_number: index + 1, feed_remaining_percent: null })))
                  else await setFieldValue('feeding_tray_controls', [])
                  await setFieldValue('feed_logged_at', null)
                  await setFieldValue('logged_date', null)
                  await setFieldValue('cycle_id', value || null)
                  await setFieldTouched('cycle_id')
                },
                titleTx: "feedingTrayControlForm.cycleIdPlaceholder"
              }}
            />
            <PickerField
              key={`${values.cycle_id}-logged_date`}
              name="logged_date"
              txPrefix="feedingTrayControlForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              pickerProps={{
                dataKey: 'feed_at',
                dataLabel: 'feed_at',
                fetchData: (search) => {
                  return Feed.fetch({
                    cycle_id__eq: values.cycle_id,
                    search,
                    sort: 'logged_at,desc'
                  }).then((feeds: Array<Feed>) => {
                    return _.map(_.groupBy(feeds, 'feed_at'), (value, key) => ({
                      feed_at: key,
                      feeds: value,
                      age: value[0].getAttribute('age')
                    }))
                  })
                },
                renderSelectedItemText: (item) => item && `${moment(item.feed_at).format("DD MMM YYYY")} (${item.age || '-'})`,
                isUseSearch: false,
                clearable: true,
                isDisabled: !values.cycle_id || initialValues.logged_date,
                onValueChange: async (value) => {
                  await setFieldValue('feed_logged_at', null)
                  await setFieldValue('logged_date', value || null)
                  await setFieldTouched('logged_date')
                },
                titleTx: "feedingTrayControlForm.loggedDatePlaceholder"
              }}
            />
            <ModelField
              key={`${values.cycle_id}-${values.logged_date}-feed_logged_at`}
              name="feed_logged_at"
              txPrefix="feedingTrayControlForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              modelPickerProps={{
                Model: Feed,
                dataKey: "logged_at",
                dataLabel: "logged_at",
                fetchData: (model) => (
                  model.fetch({
                    cycle_id__eq: values.cycle_id,
                    feed_at__eq: values.logged_date || '-',
                  }).then((results) => {
                    if (initialValues.feed_logged_at)
                      return results;
                    else
                      return FeedingTrayControl.fetch({
                        cycle_id__eq: values.cycle_id,
                        feed_logged_at__in: results.map((r) => r.logged_at).join(',')
                      }).then((fs) => {
                        const feed_logged_at = fs.map((f) => f.feed_logged_at);
                        return results.filter((r) => !feed_logged_at.includes(r.logged_at))
                      })
                  })
                ),
                renderSelectedItemText: (item) => item && `${moment.utc(item.logged_at).local().format("HH:mm")} - ${numeral(item.quantity).format('0,0.[00]')} kg` ||  "-",
                isUseSearch: false,
                clearable: true,
                isDisabled: !values.logged_date || initialValues.feed_logged_at,
                onValueChange: async (value) => {
                  await setFieldValue('logged_time', (value && moment.utc(value).local().format("HH:mm:ss")) || null)
                  await setFieldValue('feed_logged_at', value || null)
                  await setFieldTouched('feed_logged_at')
                },
                titleTx: "feedingTrayControlForm.feedLoggedAtPlaceholder"
              }}
            />
            <View flexDirection="row">
              <DateTimePickerField
                name="logged_time"
                txPrefix="feedingTrayControlForm"
                colorScheme={colorScheme}
                formControlProps={{ flex: 1, isRequired: true }}
                dateTimePickerProps={{ dateTimePickerProps: { mode: "time" } }}
              />
              <View
                flex={1}
                style={FORM_DROPDOWN}
              ></View>
            </View>
            <Text tx="feedingTrayControlForm.feedingTrayControlRemark" preset="body3" style={REMARK} />
            {!!values.feeding_tray_controls.length && <View style={TRAY_TITLE}>
              <Text>
                <Text tx="feedingTrayControls.empty" preset="body3" transformText={_.startCase} colorScheme={colorScheme} color={color.primary} bold/>
                <Text text=": <10%, " preset="body3" colorScheme={colorScheme}/>
                <Text tx="feedingTrayControls.few" preset="body3" transformText={_.startCase} colorScheme={colorScheme} color={color.palette.orange500} bold/>
                <Text text=": 10% - 50%, " preset="body3" colorScheme={colorScheme}/>
                <Text tx="feedingTrayControls.plenty" preset="body3" transformText={_.startCase} colorScheme={colorScheme} color={color.palette.red700} bold/>
                <Text text=": >50%" preset="body3" colorScheme={colorScheme}/>
              </Text>
            </View>}
          </WalkthroughElement>
          <FieldArray
            name="feeding_tray_controls"
            render={() => (
              values.feeding_tray_controls.map((f, index) => (
                index === 0 ? (
                  <WalkthroughElement
                    key={`${values.cycle_id}-walkthrough.${index}.feed_remaining_percent`}
                    id="feeding-tray-form-second"
                    scrollViewRef={scrollViewRef}
                    scrollOffset={250}
                  >
                    <View style={REF_WRAPPER}>
                      <RadioField
                        key={`${values.cycle_id}-feeding_tray_controls.${index}.feed_remaining_percent`}
                        name={`feeding_tray_controls.${index}.feed_remaining_percent`}
                        colorScheme={colorScheme}
                        formControlProps={{
                          label: translate('feedingTrayControlForm.feedingTray', { number: f.tray_number || (index + 1) }),
                          labelTx: undefined
                        }}
                        radioGroupProps={{
                          name: `feeding_tray_controls.${index}.feed_remaining_percent`,
                          value: f.feed_remaining_percent != null ? String(f.feed_remaining_percent || 0) : null,
                          onChange: async (value) => {
                            await setFieldValue(`feeding_tray_controls.${index}.feed_remaining_percent`, value !== null && value !== undefined ? Number(value) : null);
                            await setFieldTouched(`feeding_tray_controls.${index}.feed_remaining_percent`)
                          }
                        }}
                      />
                    </View>
                  </WalkthroughElement>
                ) : (
                  <RadioField
                    key={`${values.cycle_id}-feeding_tray_controls.${index}.feed_remaining_percent`}
                    name={`feeding_tray_controls.${index}.feed_remaining_percent`}
                    colorScheme={colorScheme}
                    formControlProps={{
                      label: translate('feedingTrayControlForm.feedingTray', { number: f.tray_number || (index + 1) }),
                      labelTx: undefined
                    }}
                    radioGroupProps={{
                      name: `feeding_tray_controls.${index}.feed_remaining_percent`,
                      value: f.feed_remaining_percent != null ? String(f.feed_remaining_percent || 0) : null,
                      onChange: async (value) => {
                        await setFieldValue(`feeding_tray_controls.${index}.feed_remaining_percent`, value !== null && value !== undefined ? Number(value) : null);
                        await setFieldTouched(`feeding_tray_controls.${index}.feed_remaining_percent`)
                      }
                    }}
                  />
                )
              ))
            )}
          />
          <TextField
            name="remark"
            txPrefix="feedingTrayControlForm"
            colorScheme={colorScheme}
            formControlProps={{ inputProps: { multiline: true, numberOfLines: 3, onSubmitEditing: undefined } }}
          />
        </>
      )}
    </Formik>
  )
})

FeedingTrayControlForm.displayName = 'FeedingTrayControlForm'
